import React, { useState, Fragment, useEffect, useRef, useMemo } from "react";
import {
  Table,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Input,
  Label,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { showToast } from "../ToastNotification";
import { useSelector } from "react-redux";
import CommonLoading from "./CommonLoading";
import {
  adjustSelectedDateRange,
  formatDate,
  getCurrentFiscalYearDates,
  getWorkingDateRange,
} from "./WorkingDateRange";
import {
  contractNote_listThunk,
  contractNote_singleThunk,
} from "../../slices/thunks";
import usePersistStoreData from "../Hooks/usePersistStoreData";
import { useDispatch } from "react-redux";

const TableContainerDateRangeFilter6 = ({
  footer,

  // columns,
  data,
  theadClass = "",
  tbodyClass = "",
}) => {
  const dispatch = useDispatch();
  const { userData } = usePersistStoreData();
  const token = userData?.token;
  const [globalFilter, setGlobalFilter] = useState("");

  const [sorting, setSorting] = useState([]);
  // const [dateRange, setDateRange] = useState([]);
  const { fyStart, fyEnd } = getCurrentFiscalYearDates();

  const [dynamicMaxDate, setDynamicMaxDate] = useState("today"); // Default max date is today
  const [tableLoading, setTableLoading] = useState(false);

  const { contractNote_listData, contractNote_listLoading } = useSelector(
    (state) => state.ContractNote_List
  );
  const { contractNote_singleData, contractNote_singleLoading } = useSelector(
    (state) => state.ContractNote_Single
  );
  const [activeDownload, setActiveDownload] = useState(null);
  const [exchangeFilter, setExchangeFilter] = useState(
    contractNote_listData?.summary?.contract_type || ""
  );
  const tableContainerRef = useRef(null);

  // Minimum column width
  const MIN_COLUMN_WIDTH = 120;

  // const convertToDateFormat = (dateString) => {
  //   const months = {
  //     Jan: "01",
  //     Feb: "02",
  //     Mar: "03",
  //     Apr: "04",
  //     May: "05",
  //     Jun: "06",
  //     Jul: "07",
  //     Aug: "08",
  //     Sep: "09",
  //     Oct: "10",
  //     Nov: "11",
  //     Dec: "12",
  //   };

  //   const [day, month, year] = dateString.replace(",", "").split(" ");
  //   return `${day.padStart(2, "0")}-${months[month]}-${year}`;
  // };

  // const convertToDateFormatYMD = (dateString) => {
  //   const months = {
  //     Jan: "01",
  //     Feb: "02",
  //     Mar: "03",
  //     Apr: "04",
  //     May: "05",
  //     Jun: "06",
  //     Jul: "07",
  //     Aug: "08",
  //     Sep: "09",
  //     Oct: "10",
  //     Nov: "11",
  //     Dec: "12",
  //   };

  //   const [day, month, year] = dateString.replace(",", "").split(" ");
  //   return `${year}-${months[month]}-${day.padStart(2, "0")}`;
  // };
  const convertDateFormat = (dateStr) => {
    // Assuming input is "dd-mm-yyyy"
    const [day, month, year] = dateStr.split("-");
    return `${year}-${month}-${day}`;
  };

  const handleSingleDownload = (selectedDate, contractName, contractType) => {
    setActiveDownload(contractName);

    dispatch(
      contractNote_singleThunk({
        token,

        params: {
          contract_type: contractType,
          download_type: "single",
          date: convertDateFormat(selectedDate),
          contract_name: contractName,
        },
      })
    )
      .unwrap() // if using Redux Toolkit createAsyncThunk; otherwise, chain directly
      .then((result) => {
        const blob = new Blob([result], { type: "application/pdf" });

        // Create an object URL
        const downloadUrl = window.URL.createObjectURL(blob);

        // Create a temporary anchor element
        const link = document.createElement("a");
        link.href = downloadUrl;

        // Generate filename
        const fileName = contractName.endsWith(".pdf")
          ? contractName
          : `${contractName}.pdf`;
        link.setAttribute("download", fileName);

        // Append, click, cleanup
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        showToast("Downloaded Successfully", "success");
      })
      .catch((error) => {
        showToast("Something went wrong. Please try again later", "error");
      })
      .finally(() => {
        setActiveDownload(null);
      });
  };

  const columns = useMemo(
    () => [
      { id: "trade_date", Header: "Trade Date", accessorKey: "trade_date" },
      {
        id: "contract_note_no",
        Header: "Contract Note No.",
        accessorKey: "contract_note_no",
      },
      { id: "trade_type", Header: "Trade Type", accessorKey: "trade_type" },
      {
        id: "action",
        Header: "Actions",
        accessorKey: "action",
        cell: ({ row }) => {
          // Use the contract note number (or any unique id) as identifier for download tracking
          const currentId = row.original.contract_note_no;

          return (
            <div className="d-flex align-items-center justify-content-start gap-2">
              <button
                className="btn btn-sm btn-primary centerContent"
                style={{
                  borderRadius: "5px",
                  minWidth: "100px",
                  maxHeight: "1.8rem",
                }}
                onClick={() =>
                  handleSingleDownload(
                    row.original.trade_date,
                    row.original.contract_note_no,
                    row.original.original_contract_type
                  )
                }
                // Optional: disable the button while downloading
                disabled={
                  contractNote_singleLoading && activeDownload === currentId
                }
              >
                {/* Show a spinner only if global loading is true and this button is active */}
                {contractNote_singleLoading && activeDownload === currentId ? (
                  // Render your loading spinner here. This is just an example:
                  <Spinner size="sm" />
                ) : (
                  <>
                    <span className="">
                      {/* <svg width="9" height="12" viewBox="0 0 9 12" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M9 4.23529H6.42857V0H2.57143V4.23529H0L4.5 9.17647L9 4.23529ZM3.85714 5.64706V1.41176H5.14286V5.64706H5.895L4.5 7.17882L3.105 5.64706H3.85714ZM0 10.5882H9V12H0V10.5882Z" fill="#fff"/>
  </svg> */}
                      <i className="bx bx-download fs-16  "></i>
                    </span>
                    <span className="ms-1">Download</span>
                  </>
                )}
              </button>
            </div>
          );
        },
      },
    ],
    [contractNote_singleLoading, activeDownload]
  );

  const transformData = (data) => {
    if (!data?.items || !data?.summary) return [];
    const contractTypeKey = data.summary.contract_type;
    const tradeTypeMap = {
      digital: "Digital",
      digital_com: "Digital Commodity",
    };

    return Object.entries(data.items).map(([date, contractNoteNo]) => ({
      trade_date: date,
      contract_note_no: contractNoteNo,
      trade_type: tradeTypeMap[contractTypeKey] || "-",
      original_contract_type: contractTypeKey,
    }));
  };

  const transformedRows = useMemo(() => {
    return transformData(contractNote_listData);
  }, [contractNote_listData]);

  const table = useReactTable({
    data: transformedRows,
    columns,
    contractNote_listData,
    getCoreRowModel: getCoreRowModel(),
    // getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      globalFilter,
      // sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    // onSortingChange: setSorting,
  });

  const parseDate = (dateString) => {
    if (!dateString) return null;
    // Parse the date in the format "28 Dec, 2024"
    const [day, monthStr, year] = dateString.replace(",", "").split(" ");
    const months = {
      January: 0,
      February: 1,
      March: 2,
      April: 3,
      May: 4,
      June: 5,
      July: 6,
      August: 7,
      September: 8,
      October: 9,
      November: 10,
      December: 11,
    };
    return new Date(year, months[monthStr], parseInt(day));
  };

  const initialRange = useMemo(() => {
    if (
      contractNote_listData.summary?.start &&
      contractNote_listData.summary?.end
    ) {
      return [
        parseDate(contractNote_listData.summary?.start),
        parseDate(contractNote_listData.summary?.end),
      ];
    }
    return [];
  }, [contractNote_listData.summary]);

  const [dateRange, setDateRange] = useState(() => initialRange);

  const handleContractNoteList = async () => {
    if (!dateRange || dateRange.length !== 2) {
      showToast("Please select a valid date range.", "error");
      return false;
    }

    const start_date = formatDate(dateRange[0]);
    const end_date = formatDate(dateRange[1]);

    // Dispatch the API call with the selected contract note type and the computed dates.
    await dispatch(
      contractNote_listThunk({
        token,
        params: {
          contract_type: exchangeFilter,
          start_date,
          end_date,
        },
      })
    );
  };

  // Compute column widths once for consistent sizing
  const columnWidths = useMemo(() => {
    const allColumns = table.getAllColumns();

    // Calculate total size units
    let totalSizeUnits = 0;
    allColumns.forEach((column) => {
      const size = column.columnDef.size || 1;
      totalSizeUnits += size;
    });

    // Calculate each column's percentage width
    return allColumns.map((column) => {
      const minWidth = column.columnDef.minSize || MIN_COLUMN_WIDTH;
      const size = column.columnDef.size || 1;
      const percentWidth = (size / totalSizeUnits) * 100;

      return {
        id: column.id,
        minWidth,
        percentWidth: `${percentWidth}%`,
        size,
      };
    });
  }, [table.getAllColumns()]);

  // Empty state component
  const EmptyState = () => (
    <Card className="m-0 text-muted">
      <CardBody>No Record Found</CardBody>
    </Card>
  );

  
  

  return (
    <Fragment>
      {/* Search and Date Filter */}
      <div className="mb-2 row">
        <Col
          sm="12"
          md={"6"}
          lg="6"
          xl="6"
          xxl="4"
          className=" mb-2 mb-xxl-0  "
        >
          <div className="search-box position-relative d-inline-block w-100">
            <input
              id="search-bar-0"
              type="text"
              className="form-control"
              style={{
                backgroundColor: "#F1F4F7",
                border: "1px solid transparent",
                height: "38px",
                paddingLeft: "2.5rem",
              }}
              placeholder="Search by Contract Note Number"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
            <i
              className="bx bx-search-alt search-icon"
              style={{
                position: "absolute",
                left: "1rem",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#6c757d",
              }}
            ></i>
          </div>
        </Col>
        <Col sm="12" md={"6"} lg="6" xl="6" xxl="3" className=" mb-2 mb-xxl-0 ">
          <div className="d-flex align-items-center justify-content-between">
            <Label
              htmlFor="type"
              className="form-label d-flex align-items-center  m-0 me-1 text-dark fs-14 fw-medium"
            >
              Select Contract Type:
            </Label>
            <Input
              type="select"
              id="type"
              name="type"
              className="form-control cursor-pointer"
              style={{
                backgroundColor: "#F1F4F7",
                border: "1px solid transparent",
                height: "38px",
                width: "calc(100% - 160px)", // adjust based on label width
              }}
              value={exchangeFilter}
              onChange={(e) => setExchangeFilter(e.target.value)}
            >
              <option value={"digital"}>Digital</option>
              <option value={"digital_com"}>Digital Commodity</option>
            </Input>
          </div>
        </Col>
        <div className="date-box d-inline-block col-xxl-4 col-xl-9 col-lg-8 col-md-8  col-12 mb-xxl-0  mb-2">
          <div className="d-flex align-items-center justify-content-between flex-sm-column flex-md-row">
            <Label
              htmlFor="type"
              className="form-label d-flex align-items-center me-1  m-0 text-dark fs-14 fw-medium"
            >
              Select Date Range:
            </Label>
            <Flatpickr
              style={{
                backgroundColor: "#F1F4F7",
                border: "1px solid transparent",
                width: "calc(100% - 150px)",
              }}
              options={{
                mode: "range",
                dateFormat: "d-m-Y",

                maxDate: new Date(new Date().setDate(new Date().getDate() - 1)), // T-1
                onClose: (selectedDates, dateStr, instance) => {
                  if (selectedDates.length === 1) {
                    // Reset to initial/default date range
                    setDateRange(initialRange);
                    instance.setDate(initialRange);
                    return;
                  }

                  if (selectedDates.length === 2) {
                    if (
                      selectedDates[0].getTime() === selectedDates[1].getTime()
                    ) {
                      instance.clear(); // Clear the selection
                      // Reset to initial range
                      setDateRange(initialRange);
                      instance.setDate(initialRange);
                      showToast(
                        "Please select a range with different start and end dates.",
                        "error"
                      );
                      return;
                    }

                    const diffInDays =
                      (selectedDates[1] - selectedDates[0]) /
                      (1000 * 60 * 60 * 24);
                    if (diffInDays > 29) {
                      instance.clear();
                      showToast(
                        "You can only select a maximum range of 30 days.",
                        "error"
                      );
                    }
                  }
                },
              }}
              value={dateRange.length ? dateRange : initialRange}
              onChange={(selectedDates) => {
                // Update the state to reflect the selected date range
                setDateRange(selectedDates);
              }}
              className="form-control"
            />
          </div>
          <i
            className="bx bx-calendar date-icon"
            style={{
              height: "100%",
            }}
          ></i>
        </div>
        <Col sm="12" md="4" lg="4" xl="3" xxl="1" className="mb-xxl-0  mb-2">
          <button
            className="btn btn-primary w-100"
            style={{ 
              height: "38px" ,
              minWidth:"80px"
            }}
            onClick={handleContractNoteList}
            disabled={contractNote_listLoading}
          >
            {contractNote_listLoading ? <Spinner size="sm" /> :"Apply"}
          </button>
        </Col>
      </div>

      {/* Table Container with Horizontal Scroll */}
      <div
        className="table-responsive table-container"
        ref={tableContainerRef}
        style={{
          minHeight: "150px", // Minimum height for empty/few rows
          maxHeight: "65vh", // Maximum height for many rows
          border: "1px solid #e9ecef",
          borderRadius: "4px",
          overflow: "auto",
          position: "relative",
        }}
      >
        {contractNote_listLoading ? (
          <Card className="m-0 text-muted h-100">
            <CardBody className="d-flex align-items-center justify-content-center h-100">
              <CommonLoading
                style={{
                  maxHeight: "6vh",
                }}
              />
            </CardBody>
          </Card>
        ) : (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: "800px", // Set a minimum width to ensure horizontal scrolling
            }}
          >
            {/* Table Header */}
            <thead
              className={`bg-gl-primary sticky-header ${theadClass}`}
              style={{
                position: "sticky",
                top: 0,
                zIndex: 2,
                boxShadow: "0 2px 3px rgba(0,0,0,0.1)",
              }}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => {
                    const isSorted = header.column.getIsSorted();
                    const colWidth = columnWidths.find(
                      (col) => col.id === header.id
                    );

                    return (
                      <th
                        key={header.id}
                        style={{
                          width: colWidth?.percentWidth,
                          minWidth: `${colWidth?.minWidth}px`,
                          padding: "0.4rem",
                          fontWeight: "bold",
                          color: "#fff",
                          borderRight:
                            index < headerGroup.headers.length - 1
                              ? "1px solid rgba(255,255,255,0.2)"
                              : "none",
                          textAlign:
                            header.column.columnDef.headerTextAlign || "center",
                          // cursor: "pointer",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "normal",
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          {flexRender(
                            header.column.columnDef.Header,
                            header.getContext()
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>

            {/* Table Body */}
            <tbody className={tbodyClass}>
              {transformedRows?.length > 0 &&
              table.getRowModel()?.rows?.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    style={{ borderBottom: "1px solid #e9ecef" }}
                  >
                    {row.getVisibleCells().map((cell, index) => {
                      const colWidth = columnWidths.find(
                        (col) => col.id === cell.column.id
                      );

                      return (
                        <td
                          key={cell.id}
                          className="fs-p-bg fw-medium"
                          style={{
                            width: colWidth?.percentWidth,
                            minWidth: `${colWidth?.minWidth}px`,
                            padding: "0.75rem",

                            borderRight:
                              index < row.getVisibleCells().length - 1
                                ? "1px solid #e9ecef"
                                : "none",
                            textAlign:
                              cell.column.columnDef.bodyTextAlign || "left",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                           
                            whiteSpace: "normal",
                            padding: "0.3rem",
                            paddingLeft: "0.4rem"
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={table.getAllColumns().length}
                    className="p-4 text-center"
                  >
                    <EmptyState />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </Fragment>
  );
};

export default TableContainerDateRangeFilter6;
